import {catchError, defer, distinctUntilChanged, map, Observable, of, shareReplay, startWith, switchMap} from 'rxjs';
import type {MeasurementsResult, SourceIds} from '@flowmaps-com/flowmaps-typescriptmodels';
import type {CompletenessInfo} from '../../app-context';

export interface CrremQueryContext {
    sources: SourceIds;
    locationId: string | null;
    locationDashboard: boolean;
    meterDashboard: boolean;
}

export interface CrremMeasurementsResult {
    sourceKey: string;
    locationId: string | null;
    measurements?: MeasurementsResult;
    coverage?: Record<number, CompletenessInfo[]>;
    loading?: boolean;
    failed?: boolean;
    unavailable?: boolean;
}

// The local CQRS query returns this descriptor once. Its caller owns the stream subscription.
export interface CrremMeasurementChannel {
    measurements$: Observable<CrremMeasurementsResult>;
}

function sourceIdentity(context: CrremQueryContext) {
    const ids = context.sources;
    const locations = ids.locationIds || [];
    const otherIds = [...(ids.organisationIds || []), ...(ids.connectionIds || []), ...(ids.meterIds || [])];
    const locationId = context.locationDashboard && !context.meterDashboard
        && locations.includes(context.locationId) && !otherIds.length ? context.locationId : null;
    return {locationId, sourceKey: JSON.stringify({
        locationId, locations: [...locations].sort(), others: otherIds.sort(),
        locationDashboard: context.locationDashboard, meterDashboard: context.meterDashboard
    })};
}

export function createCrremMeasurementStream(
    contexts$: Observable<CrremQueryContext>, refresh$: Observable<void>,
    load: (sources: SourceIds) => Observable<Pick<CrremMeasurementsResult, 'measurements' | 'coverage'>>
): Observable<CrremMeasurementsResult> {
    return contexts$.pipe(
        map(context => ({context, identity: sourceIdentity(context)})),
        distinctUntilChanged((a, b) => a.identity.sourceKey === b.identity.sourceKey),
        switchMap(({context, identity}) => {
            if (!identity.locationId) return of({...identity, unavailable: true});
            return refresh$.pipe(startWith(undefined), switchMap(() => defer(() => load(context.sources)).pipe(
                map(data => ({...data, ...identity})),
                catchError(() => of({...identity, failed: true})),
                startWith({...identity, loading: true})
            )));
        }),
        shareReplay({bufferSize: 1, refCount: true})
    );
}

export function crremDataMatchesLocation(data: CrremMeasurementsResult, locationId: string): boolean {
    return !!locationId && data.locationId === locationId;
}
